<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="810px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="handleClose"
    custom-class="full-feedback-dialog"
  >
    <div slot="title" class="dialog-header">
      <div class="dialog-title">提交建议</div>
      <div class="dialog-subtitle">您的反馈是我们进步的动力</div>
    </div>
    <div class="dialog-content">
      <div class="form-section">
        <div class="form-label">反馈类型</div>
        <div class="feedback-type-buttons">
          <el-button 
            v-for="type in feedbackTypes" 
            :key="type.value"
            :type="form.feedbackType === type.value ? 'primary' : 'default'"
            @click="form.feedbackType = type.value"
          >{{ type.label }}</el-button>
        </div>
      </div>
      <div class="form-section">
        <div class="form-label">标题<span class="required">*</span></div>
        <el-input v-model="form.title" placeholder="请输入建议标题" class="title-input" maxlength="200"></el-input>
        <div class="title-tags">
          常用标题标签：
          <el-tag 
            v-for="tag in titleTags" 
            :key="tag"
            size="small"
            @click="selectTitleTag(tag)"
            class="title-tag"
          >{{ tag }}</el-tag>
        </div>
      </div>
      <div class="form-section">
        <div class="form-label">反馈内容<span class="required">*</span></div>
        <el-input
          v-model="form.content"
          type="textarea"
          :rows="6"
          maxlength="8000"
          placeholder="请详细描述您的建议或遇到的问题..."
          class="content-textarea"
          @paste.native="handlePasteEvent"
        ></el-input>
      </div>
      <div class="form-section">
        <div class="form-label">附件上传</div>
        <div class="upload-area">
          <div class="upload_wrapper">
            <div class="upload-placeholder" @click="triggerImageUpload">
              <i class="el-icon-plus"></i>
              <div class="upload-text">上传图片</div>
            </div>
            <div v-if="fileList.length > 0" class="uploaded-images-preview-container">
              <div v-for="(file, index) in fileList" :key="file.uid || index" class="uploaded-item">
                <div v-if="file.loading" class="image-loading-state">
                  <div class="loading-spinner"></div>
                  <div class="image-info">
                    <div class="image-name" :title="file.name">{{ file.name }}</div>
                    <div class="image-size">{{ formatFileSize(file.size) }}</div>
                  </div>
                </div>
                <div v-else class="uploaded-image-item">
                  <img :src="file.ossUrl || file.url" :alt="file.name" :title="file.name" class="preview-image" />
                </div>
                <button class="remove-image-btn" @click="removeUploadedImage(index)">
                  <i class="el-icon-close"></i>
                </button>
              </div>
            </div>
            <!-- 隐藏的文件输入框 -->
            <input ref="imageInput" type="file" accept=".png,.jpg,.jpeg" multiple style="display: none"
              @change="handleImageUpload" />
          </div>
          <div class="upload-tip">支持png, jpg, jpeg图片格式，最多上传5张图片</div>
        </div>
      </div>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSubmit" :disabled="imageUploading || submitting">提交反馈</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { uploadImageToOSS, addFeedbackApi } from '@/api/ai/agent';
import { validateImageByMagicNumber } from '@/utils/imageHelper';
export default {
  name: "FullFeedbackDialog",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    userId: {
      type: [String, Number],
      default: ''
    },
    content: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // 本地弹窗状态
      dialogVisible: this.visible,
      form: {
        title: '',
        content: '',
        feedbackType: 'suggestion'
      },
      fileList: [],
      // 反馈类型选项
      feedbackTypes: [
        { label: '功能建议', value: 'suggestion' },
        { label: '系统问题', value: 'bug' },
        { label: '其他', value: 'other' }
      ],
      // 常用标题标签
      titleTags: ['功能优化', '界面美化', '运行速度', '其他'],
      // 图片上传状态
      imageUploading: false,
      // 提交状态
      submitting: false,
      // 提示消息控制
      lastWarningTime: 0
    }
  },
  watch: {
    // 监听父组件传递的visible变化
    visible: {
      handler(newVal) {
        this.dialogVisible = newVal;
      },
      immediate: true
    },
    // 监听父组件传递的content变化
    content: {
      handler(newVal) {
        this.form.content = newVal;
      },
      immediate: true
    },
    // 监听本地dialogVisible变化，通知父组件
    dialogVisible(newVal) {
      this.$emit('update:visible', newVal);
      if (!newVal) {
        this.$emit('close');
      }
    }
  },
  methods: {
    // 格式化文件大小
    formatFileSize(size) {
      if (size < 1024) {
        return size + ' B';
      } else if (size < 1024 * 1024) {
        return (size / 1024).toFixed(2) + ' KB';
      } else {
        return (size / (1024 * 1024)).toFixed(2) + ' MB';
      }
    },
    // 触发图片上传
    triggerImageUpload() {
      this.$refs.imageInput.click();
    },
    // 通过Magic Number验证文件是否为真正的图片

    // 处理图片上传
    async handleImageUpload(event) {
      const allfiles = Array.from(event.target.files);
      
      // 检查是否超过5张图片
      if (this.fileList.length + allfiles.length > 5) {
        const remainingSlots = 5 - this.fileList.length;
        if (remainingSlots <= 0) {
          this.$message.warning('最多只能上传5张图片');
          event.target.value = ''; // 清空上传控件
          return;
        }
        // 只保留前remainingSlots张图片
        const files = allfiles.slice(0, remainingSlots);
        
        // 如果用户选择的图片数量超过剩余可上传数量，给出提示
        if (allfiles.length > remainingSlots) {
          this.$message.warning('最多只能上传5张图片，已自动选取前' + remainingSlots + '张');
        }
        
        await this.processFiles(files);
        event.target.value = ''; // 清空上传控件
        return;
      }
      
      await this.processFiles(allfiles);
      event.target.value = ''; // 清空上传控件
    },
    // 处理文件列表
    async processFiles(files) {
      if (files.length === 0) {
        return;
      }
      
      this.imageUploading = true;
      let hasError = false;
      
      // 对每个文件进行处理
      for (const file of files) {
        // 验证文件大小（限制为10MB）
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
          this.$message.closeAll();
          this.$message.error(`图片 ${file.name} 大小不能超过10MB`);
          hasError = true;
          continue;
        }
        
        // 更健壮的图片验证：结合扩展名、MIME类型和Magic Number检查
        const validExtensions = ['.jpg', '.jpeg', '.png', '.gif'];
        const fileName = file.name.toLowerCase();
        const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
        const isImageMimeType = file.type.startsWith('image/');
        const isValidImage = await validateImageByMagicNumber(file);
        
        if (!hasValidExtension || !isImageMimeType || !isValidImage) {
          this.$message.closeAll();
          this.$message.error(`图片 ${file.name} 不是有效的图片格式(png, jpg, jpeg, gif)`);
          hasError = true;
          continue;
        }
        
        try {
          // 创建临时预览URL
          const previewUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = () => reject(new Error('图片读取失败'));
            reader.readAsDataURL(file);
          });
          
          // 立即创建图片对象并添加到上传数组，设置loading为true
          const imageObj = {
            loading: true,  // 初始设置为上传中
            url: previewUrl, // 使用本地预览URL
            ossUrl: null,    // 稍后填充服务器URL
            name: file.name,
            size: file.size
          };
          // 立即添加到预览列表，这样用户可以立即看到loading状态
          this.fileList.push(imageObj);
          // 创建FormData对象
          const formData = new FormData();
          formData.append('file', file);
          // 添加user_id和business_type参数
          formData.append('user_id', this.userId || '');
          formData.append('business_type', 'submit'); // 答复到时传"reply"
          
          // 调用上传图片到OSS的API
          const response = await uploadImageToOSS(formData);
          if (!response.ok) {
            throw new Error('上传失败');
          }
          
          // 解析响应
          const result = await response.json();
          const imageUrl = result.data && result.data.image_url ? result.data.image_url : null;
          if (!imageUrl) {
            throw new Error('图片URL获取失败');
          }
          // 上传成功后更新状态
          imageObj.loading = false;
          imageObj.ossUrl = imageUrl;
          imageObj.url = imageUrl; // 使用服务器返回的URL
          
        } catch (error) {
          this.$message.error(`图片 ${file.name} 上传失败: ${error.message}`);
          hasError = true;
          // 从上传列表中移除失败的图片
          const index = this.fileList.findIndex(img => img.name === file.name && img.loading === true);
          if (index > -1) {
            this.fileList.splice(index, 1);
          }
        }
      }
      
      // 更新状态
      this.imageUploading = false;
    },
    // 处理粘贴事件
    handlePasteEvent(event) {
      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) {
        console.warn("无法访问剪贴板数据");
        return;
      }
      const items = clipboardData.items;
      if (!items || items.length === 0) { return; }
      let selectedImage = null;
      let selectedIndex = -1;
      for (let i = items.length - 1; i >= 0; i--) {
        const item = items[i];
        if (item && item.type && item.type.includes("image")) {
          try {
            const file = item.getAsFile();
            if (file && file.size > 0) {
              selectedImage = file;
              selectedIndex = i;
              break;
            }
          } catch (error) {
            console.warn("无法获取文件: " + error.message);
          }
        }
      }
      if (selectedImage) {
        if (this.imageUploading) {
          const now = Date.now();
          if (now - this.lastWarningTime > 3000) {
            this.$message.warning("图片正在上传中，请稍后再试");
            this.lastWarningTime = now;
          }
          return;
        }
        const currentCount = Array.isArray(this.fileList) ? this.fileList.length : 0;
        if (currentCount >= 5) {
          const now = Date.now();
          if (now - this.lastWarningTime > 3000) {
            this.$message.warning("最多只能上传5张图片");
            this.lastWarningTime = now;
          }
          return;
        }
        event.preventDefault();
        this.uploadSingleImage(selectedImage);
      }
    },
    // 单独的上传方法
    async uploadSingleImage(file) {
      try {
        const timestamp = Date.now();
        const fileExt = file.name.lastIndexOf('.') > -1 ? file.name.slice(file.name.lastIndexOf('.')) : '';
        const fileNameWithoutExt = file.name.lastIndexOf('.') > -1 ? file.name.slice(0, file.name.lastIndexOf('.')) : file.name;
        const newFileName = `${fileNameWithoutExt}_${timestamp}${fileExt}`;
        const newFile = new File([file], newFileName, { type: file.type });
        const mockEvent = {
          target: {
            files: [newFile],
            value: ''
          }
        };
        await this.handleImageUpload(mockEvent);
      } catch (error) {
        console.error('上传失败:', error);
        this.$message.error('图片上传失败');
      }
    },
    // 移除上传的图片
    removeUploadedImage(index = null) {
      if (index !== null) {
        this.fileList.splice(index, 1);
      } else {
        this.fileList = [];
      }
      this.$message({
        type: "success",
        message: index !== null ? '图片已移除' : '所有图片已移除',
        duration: 1000
      });
    },
    // 选择标题标签
    selectTitleTag(tag) {
      if (this.form.title) {
        this.form.title += ' #' + tag;
      } else {
        this.form.title = '#' + tag;
      }
    },
    // 提交表单
    async handleSubmit() {
      if (this.submitting) return;
      
      if (!this.form.title || !this.form.title.trim()) {
        const now = Date.now();
        if (now - this.lastWarningTime > 1000) {
          this.$message({
            type: 'warning',
            message: '请填写反馈标题',
            duration: 1000
          });
          this.lastWarningTime = now;
        }
        return;
      }
      if (!this.form.content || !this.form.content.trim()) {
        const now = Date.now();
        if (now - this.lastWarningTime > 1000) {
          this.$message({
            type: 'warning',
            message: '请填写反馈内容',
            duration: 1000
          });
          this.lastWarningTime = now;
        }
        return;
      }
      
      // 映射反馈类型
      const fbTypeMap = {
        'suggestion': 'module',
        'bug': 'system',
        'other': 'other'
      };
      
      // 收集图片链接
      let attachment = [];
      if (this.fileList && this.fileList.length > 0) {
        attachment = this.fileList.map(image => image.ossUrl);
      }
      
      // 构建提交参数
      const params = {
        user_id: this.userId,
        fb_type: fbTypeMap[this.form.feedbackType] || 'other',
        title: this.form.title,
        content: this.form.content,
        fb_module: 'system',
        attachment: attachment
      };
      
      try {
        this.submitting = true;
        // 调用提交反馈接口
        const response = await addFeedbackApi(params);
        if (response && response.code === 200) {
          this.$message.success('提交成功，我们会尽快回复您');
          // 重置表单
          this.resetForm();
          // 关闭弹窗
          this.dialogVisible = false;
        } else {
          this.$message.error(response.message || '提交失败，请稍后重试');
        }
      } catch (error) {
        console.error('提交反馈失败:', error);
        this.$message.error('提交失败，请稍后重试');
      } finally {
        // 无论成功还是失败，都在1秒后恢复提交状态
        setTimeout(() => {
          this.submitting = false;
        }, 1000);
      }
    },
    handleClose() {
      this.resetForm();
      this.dialogVisible = false;
    },
    // 重置表单
    resetForm() {
      this.form.title = '';
      this.form.content = '';
      this.form.feedbackType = 'suggestion';
      this.fileList = [];
      this.imageUploading = false;
      this.submitting = false;
    }
  },
  beforeDestroy() {
    // 组件销毁时清空文件列表
    this.fileList = [];
  }
}
</script>
<style scoped lang="less">
/* 弹窗样式 */
.dialog-footer {
  text-align: right;
}
.dialog-content {
  padding:10px 0 0 0;
  border-top: 1px solid #eee;
}
/* 弹窗标题样式 */
.dialog-header {
  padding: 0;
}
.dialog-title {
  font-size: 18px;
  color: #666;
}
.dialog-subtitle {
  font-size: 12px;
  color: #409EFF;
  margin-top: 4px;
}
.form-section {
  margin-bottom: 10px;
}
.form-label {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #666;
}
/* 反馈类型按钮样式 */
.feedback-type-buttons {
  display: flex;
  .el-button {
    padding: 8px 20px;
  }
}
/* 标题输入框样式 */
.title-input {
  width: 100%;
  margin-bottom: 12px;
}
/* 标题标签样式 */
.title-tags {
  font-size: 12px;
  margin-top: 0px;
  .title-tag {
    margin-right: 8px;
    cursor: pointer;
    border: 1px solid #f1f1f1;
    color: #666;
    background-color: #f8f8f8;
    &:hover {
      background-color: #ecf5ff;
      color: #409eff;
      border: 1px solid #ecf5ff;
    }
    &.is-selected {
      background-color: #ecf5ff;
      color: #409eff;
      border: 1px solid #409eff;
    }
  }
}
/* 必填星号样式 */
.required {
  color: #F56C6C;
  margin-left: 4px;
  vertical-align: middle;
}
/* 内容输入框样式 */
.content-textarea {
  width: 100%;
}
/* 上传区域样式 */
.upload-area {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 4px;
  flex-wrap: nowrap;
}
.upload_wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-shrink: 0;
}
.upload_wrapper .upload-placeholder {
  width: 118px;
  height: 118px;
  border: 1px dashed #DCDFE6;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  margin-right:10px;
  &:hover {
    border-color: #409EFF;
    background-color: #ECF5FF;
  }
  .el-icon-plus {
    font-size: 24px;
    color: #999;
    margin-bottom: 8px;
  }
  .upload-text {
    font-size: 12px;
    color: #999;
  }
}
.upload-tip {
  margin-top: 0px;
  font-size: 12px;
  color: #999;
}
/* 已上传文件样式 */
.uploaded-images-preview-container {
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  align-items: flex-start;
  flex: 1;
  overflow-x: auto;
}

.uploaded-item {
  position: relative;
  flex-shrink: 0;
}

/* 图片上传中的 loading 状态 */
.image-loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 10px;
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background-color: #f5f7fa;
}

/* Loading 动画 */
.loading-spinner {
  width: 30px;
  height: 30px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #409EFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 图片信息 */
.image-info {
  width: 100%;
  text-align: center;
  overflow: hidden;
}

.image-name {
  font-size: 12px;
  color: #333;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-size {
  font-size: 11px;
  color: #999;
  text-align: center;
}

.uploaded-image-item {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.remove-image-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #f78989;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  opacity: 0;
  transition: all 0.3s ease;
  transform: scale(1);
}
.uploaded-item:hover .remove-image-btn {
  opacity: 0.8;
}
.uploaded-item:hover .remove-image-btn:hover {
  opacity: 1;
  transform: scale(1.2);
}
</style>
<style lang="less">
/* 确保弹窗层级高于抽屉 */
.full-feedback-dialog {
  margin-top: 60px !important;
  .el-dialog__header {
    padding: 20px 15px 5px 20px;
  }
  .el-dialog__body {
    padding: 0px 20px;
  }
}
</style>